import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Badge, Button, EmptyStateOnboarding, ExternalLink } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { OctoLink } from "@octopusdeploy/utilities";
import { useCallback, useMemo } from "react";
import RequestFreeTrialUpsellCallout from "~/components/Callout/RequestFreeTrialUpsellCallout";
import { EmptyStateDeploymentFreezesIllustration } from "~/components/DeploymentFreezes/EmptyStateDeploymentFreezesIllustration";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
interface EmptyDeploymentFreezesViewProps {
    globalDeploymentFreezeEnabled: boolean;
    isTrial: boolean;
}
const description = (<span>
        Coordinating freezes project by project often leads to inconsistent enforcement and manual overhead. Global Deployment Freezes let you automatically block deployments across multiple projects, so nothing ships during high-risk periods.
    </span>);
export function DeploymentFreezesEmptyState({ globalDeploymentFreezeEnabled, isTrial }: EmptyDeploymentFreezesViewProps) {
    return <UpsellEmptyState globalDeploymentFreezeEnabled={globalDeploymentFreezeEnabled} isTrial={isTrial}/>;
}
function UpsellEmptyState({ globalDeploymentFreezeEnabled, isTrial }: EmptyDeploymentFreezesViewProps) {
    const onRequestTrial = useCallback(async (repository: Repository) => await repository.DeploymentFreezes.requestTrial(), []);
    const disableAddDeploymentFreezeButton = useMemo(() => !isAllowed({ permission: Permission.DeploymentFreezeAdminister }), []);
    const primaryAction: PrimaryPageAction = useMemo(() => ({
        type: "custom",
        key: "request-trial",
        content: (<>
                    {globalDeploymentFreezeEnabled ? (<>
                            <Button importance="primary" label="Add Global Deployment Freeze" disabled={disableAddDeploymentFreezeButton} href={links.createDeploymentFreezePage.generateUrl()} title={"Add Deployment Freeze"}/>
                            <ExternalLink href="DeploymentFreezes">Learn more</ExternalLink>
                        </>) : (<RequestFreeTrialUpsellCallout featureName={"Deployment Freezes"} onRequestTrial={onRequestTrial} docsLink={OctoLink.Create("DeploymentFreezes")} docsLabel={"Docs: Deployment Freezes"} dialogTitle={"Request a free trial of global Deployment Freezes"}>
                            <div className={styles.calloutContent}>
                                <p>Request a free trial of global Deployment Freezes.</p>
                                <p>Or, create project-level Deployment Freezes by opening a project and selecting Freezes from the navigation.</p>
                            </div>
                        </RequestFreeTrialUpsellCallout>)}
                </>),
    }), [disableAddDeploymentFreezeButton, globalDeploymentFreezeEnabled, onRequestTrial]);
    return (<PageContent header={{ title: "" }}>
            <EmptyStateOnboarding title={"Prevent deployments across multiple projects with global Deployment Freezes"} primaryAction={primaryAction} description={description} image={<EmptyStateDeploymentFreezesIllustration />} headerBadge={isTrial ? <Badge size="medium" variant="upsell" label="Enterprise"/> : undefined}/>
        </PageContent>);
}
const styles = {
    calloutContent: css({
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: space[6],
        "& p": {
            margin: 0,
        },
    }),
};
